import { useMemo } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import type { AnalyticsPeriod } from '../types';
import { formatCurrency } from '../utils/format';
import { LEGACY_TO_PRIME, PRIME_CATEGORIES } from '../utils/categorization';

interface Props {
  periods: AnalyticsPeriod[];
  groupBy?: 'main' | 'category';
}

const PRIME_COLOR: Record<string, string> = {};
for (const p of PRIME_CATEGORIES) PRIME_COLOR[p.id] = p.color;

const CATEGORY_PALETTE = [
  '#f43f5e', '#fb923c', '#fbbf24', '#a3e635', '#34d399',
  '#22d3ee', '#60a5fa', '#a78bfa', '#f472b6', '#e879f9',
  '#fb7185', '#38bdf8', '#818cf8', '#4ade80', '#facc15',
  '#f97316', '#06b6d4', '#8b5cf6', '#ec4899', '#14b8a6',
  '#ef4444', '#0ea5e9', '#6366f1', '#10b981', '#eab308',
];

function hashString(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export function SpendingPieChart({ periods, groupBy = 'category' }: Props) {
  const data = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const p of periods) {
      for (const [cat, amt] of Object.entries(p.categories)) {
        if (groupBy === 'main') {
          const prime = (LEGACY_TO_PRIME[cat] ?? 'others').toLowerCase();
          totals[prime] = (totals[prime] ?? 0) + amt;
        } else {
          totals[cat] = (totals[cat] ?? 0) + amt;
        }
      }
    }
    const entries = Object.entries(totals)
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1]);

    return entries.map(([name, value]) => {
      const color =
        groupBy === 'main'
          ? PRIME_COLOR[name] ?? '#52525b'
          : CATEGORY_PALETTE[hashString(name) % CATEGORY_PALETTE.length];
      return { name, value: Math.round(value * 100) / 100, color };
    });
  }, [periods, groupBy]);

  if (data.length === 0) {
    return (
      <div className="flex h-[300px] items-center justify-center text-sm text-zinc-600">
        No spending data for this timeframe yet.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius={55}
          outerRadius={95}
          paddingAngle={2}
          stroke="#0a0a0a"
          strokeWidth={2}
        >
          {data.map((d) => (
            <Cell key={d.name} fill={d.color} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{
            backgroundColor: '#0a0a0a',
            border: '1px solid #27272a',
            borderRadius: 10,
            fontSize: 12,
          }}
          formatter={(value: unknown, name: unknown) => [formatCurrency(Number(value)), String(name)]}
        />
        <Legend
          wrapperStyle={{ fontSize: 11, color: '#a1a1aa' }}
          iconType="circle"
          iconSize={9}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}
